import { useEffect, useState } from "react";
import { Loader2, Save, Info, Upload, BookOpen, MapPin, Crosshair, Search } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "border border-brand-line rounded-xl px-4 py-2.5 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none bg-white text-brand-ink";
const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-brand-line rounded-xl p-6 space-y-4">
      <div>
        <h2 className="font-heading font-bold text-xl text-brand-ink">{title}</h2>
        {subtitle && <p className="text-sm text-brand-muted mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export default function Settings() {
  const [pay, setPay] = useState(null);
  const [site, setSite] = useState(null);
  const [deliv, setDeliv] = useState(null);
  const [savingPay, setSavingPay] = useState(false);
  const [savingSite, setSavingSite] = useState(false);
  const [savingDeliv, setSavingDeliv] = useState(false);
  const [att, setAtt] = useState({ lat: null, lng: null, radius_m: 200, configured: false });
  const [savingAtt, setSavingAtt] = useState(false);
  const [locating, setLocating] = useState(false);
  const [comm, setComm] = useState({ vendeur_rate: 5 });
  const [savingComm, setSavingComm] = useState(false);
  const [caisse, setCaisse] = useState({ alert_threshold: 50000 });
  const [savingCaisse, setSavingCaisse] = useState(false);

  useEffect(() => {
    api.get("/settings/payment").then(({ data }) => setPay({
      beneficiary_name: data.beneficiary_name || "", tmoney_number: data.tmoney_number || "",
      flooz_number: data.flooz_number || "", instructions: data.instructions || "",
      online_payment_enabled: data.online_payment_enabled ?? true,
    })).catch(() => setPay({ beneficiary_name: "", tmoney_number: "", flooz_number: "", instructions: "", online_payment_enabled: true }));
    api.get("/settings/site").then(({ data }) => setSite({
      author_name: data.author_name || "", author_bio: data.author_bio || "",
      tiktok_ministry_url: data.tiktok_ministry_url || "", tiktok_boutique_url: data.tiktok_boutique_url || "",
      youtube_channel_url: data.youtube_channel_url || "", bible_app_note: data.bible_app_note || "",
      youtube_videos: (data.youtube_videos || []).join("\n"),
      books: (data.books || []).length ? data.books : [{ title: "", subtitle: "", amazon_url: "", cover_path: "" }],
    })).catch(() => setSite(null));
    api.get("/settings/delivery").then(({ data }) => setDeliv({
      whatsapp_number: data.whatsapp_number || "", other_zone_note: data.other_zone_note || "",
      free_delivery_threshold: data.free_delivery_threshold ?? 0,
      zones: data.zones || [],
    })).catch(() => setDeliv(null));
    api.get("/settings/attendance").then(({ data }) => setAtt({
      lat: data.lat, lng: data.lng, radius_m: data.radius_m ?? 200, configured: data.configured,
    })).catch(() => {});
    api.get("/settings/commission").then(({ data }) => setComm({ vendeur_rate: data.vendeur_rate ?? 5 })).catch(() => {});
    api.get("/settings/caisse").then(({ data }) => setCaisse({ alert_threshold: data.alert_threshold ?? 50000 })).catch(() => {});
    loadPromos();
  }, []);

  const [promos, setPromos] = useState([]);
  const [newPromo, setNewPromo] = useState({ code: "", type: "percent", value: "", min_order: "", expires_at: "" });
  const loadPromos = () => api.get("/admin/promo-codes").then(({ data }) => setPromos(data)).catch(() => {});

  const addPromo = async () => {
    if (!newPromo.code.trim() || !newPromo.value) return toast.error("Code et valeur requis");
    try {
      await api.post("/admin/promo-codes", {
        code: newPromo.code.trim(), type: newPromo.type, value: Number(newPromo.value),
        min_order: Number(newPromo.min_order) || 0, expires_at: newPromo.expires_at || "",
      });
      toast.success("Code promo créé");
      setNewPromo({ code: "", type: "percent", value: "", min_order: "", expires_at: "" });
      loadPromos();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deletePromo = async (id) => {
    if (!window.confirm("Supprimer ce code ?")) return;
    try { await api.delete(`/admin/promo-codes/${id}`); loadPromos(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const captureLocation = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation indisponible sur cet appareil.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setAtt((a) => ({ ...a, lat: pos.coords.latitude, lng: pos.coords.longitude })); setLocating(false); toast.success("Position captée. Enregistrez pour valider."); },
      () => { setLocating(false); toast.error("Autorisez la localisation puis réessayez."); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const [addrQuery, setAddrQuery] = useState("");
  const [addrResults, setAddrResults] = useState(null);
  const [searchingAddr, setSearchingAddr] = useState(false);

  const searchAddress = async () => {
    if (addrQuery.trim().length < 3) return toast.error("Saisissez une adresse (min. 3 caractères).");
    setSearchingAddr(true);
    setAddrResults(null);
    try {
      const { data } = await api.post("/admin/settings/attendance/geocode", { query: addrQuery.trim() });
      setAddrResults(data.results);
      if (!data.results.length) toast.info(data.message || "Aucun résultat. Essayez une adresse plus précise ou saisissez les coordonnées manuellement.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSearchingAddr(false); }
  };

  const pickAddress = (r) => {
    setAtt((a) => ({ ...a, lat: r.lat, lng: r.lng }));
    setAddrResults(null);
    toast.success("Coordonnées définies. Enregistrez pour valider.");
  };

  const saveCaisse = async () => {
    setSavingCaisse(true);
    try {
      await api.put("/admin/settings/caisse", { alert_threshold: Number(caisse.alert_threshold) || 0 });
      toast.success("Seuil d'alerte caisse enregistré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCaisse(false); }
  };

  const saveComm = async () => {
    setSavingComm(true);
    try {
      await api.put("/admin/settings/commission", { vendeur_rate: Number(comm.vendeur_rate) || 0 });
      toast.success("Commission vendeur enregistrée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingComm(false); }
  };

  const saveAtt = async () => {    if (att.lat == null || att.lng == null) return toast.error("Captez d'abord la position du service.");
    setSavingAtt(true);
    try {
      await api.put("/admin/settings/attendance", { lat: att.lat, lng: att.lng, radius_m: Number(att.radius_m) || 200 });
      setAtt((a) => ({ ...a, configured: true }));
      toast.success("Lieu de pointage enregistré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingAtt(false); }
  };

  const saveDeliv = async () => {
    setSavingDeliv(true);
    try {
      await api.put("/admin/settings/delivery", { ...deliv, free_delivery_threshold: Number(deliv.free_delivery_threshold) || 0, zones: deliv.zones.map((z) => ({ ...z, fee: Number(z.fee) })) });
      toast.success("Livraison enregistrée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingDeliv(false); }
  };

  const setZone = (i, k) => (e) => setDeliv((d) => { const zones = [...d.zones]; zones[i] = { ...zones[i], [k]: e.target.value }; return { ...d, zones }; });
  const setD = (k) => (e) => setDeliv({ ...deliv, [k]: e.target.value });

  const savePay = async () => {
    setSavingPay(true);
    try { await api.put("/admin/settings/payment", pay); toast.success("Paiement enregistré"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPay(false); }
  };

  const saveSite = async () => {
    setSavingSite(true);
    try {
      const payload = { ...site, youtube_videos: site.youtube_videos.split("\n").map((s) => s.trim()).filter(Boolean) };
      await api.put("/admin/settings/site", payload);
      toast.success("Contenu ministère enregistré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingSite(false); }
  };

  const uploadCover = (i) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSite((s) => { const books = [...s.books]; books[i] = { ...books[i], cover_path: data.path }; return { ...s, books }; });
      toast.success("Couverture ajoutée");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setBook = (i, k) => (e) => setSite((s) => { const books = [...s.books]; books[i] = { ...books[i], [k]: e.target.value }; return { ...s, books }; });
  const setS = (k) => (e) => setSite({ ...site, [k]: e.target.value });
  const setP = (k) => (e) => setPay({ ...pay, [k]: e.target.value });

  if (!pay || !site || !deliv) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Paiement & réglages</h1>
        <p className="text-brand-muted mt-1">Boutique et contenu du ministère.</p>
      </div>

      <Card title="Paiement boutique (Tmoney / Flooz)" subtitle="Numéros affichés au client au moment de payer.">
        <div>
          <label className="text-sm font-medium text-brand-sub">Nom du bénéficiaire</label>
          <input data-testid="settings-beneficiary" className={inputCls + " mt-1"} value={pay.beneficiary_name} onChange={setP("beneficiary_name")} placeholder="Ex: Noumabou / Vital" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-brand-sub">Numéro Tmoney</label><input data-testid="settings-tmoney" className={inputCls + " mt-1"} value={pay.tmoney_number} onChange={setP("tmoney_number")} placeholder="+228 ..." /></div>
          <div><label className="text-sm font-medium text-brand-sub">Numéro Flooz</label><input data-testid="settings-flooz" className={inputCls + " mt-1"} value={pay.flooz_number} onChange={setP("flooz_number")} placeholder="+228 ..." /></div>
        </div>
        <div><label className="text-sm font-medium text-brand-sub">Instructions</label><textarea rows={2} className={inputCls + " mt-1"} value={pay.instructions} onChange={setP("instructions")} /></div>
        <div className="flex items-start justify-between gap-4 bg-brand-cream rounded-xl p-4">
          <div>
            <p className="font-semibold text-brand-ink text-sm">Paiement en ligne (PayGate — Flooz / T-Money)</p>
            <p className="text-xs text-brand-muted mt-0.5">Activez uniquement quand votre compte PayGate est validé. Désactivé, les clients paient en espèces ou Mobile Money manuel (avec preuve).</p>
          </div>
          <button
            type="button"
            data-testid="online-payment-toggle"
            onClick={() => setPay({ ...pay, online_payment_enabled: !pay.online_payment_enabled })}
            className={`relative w-14 h-7 rounded-full transition-colors shrink-0 ${pay.online_payment_enabled ? "bg-green-500" : "bg-slate-300"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${pay.online_payment_enabled ? "translate-x-7" : ""}`} />
          </button>
        </div>
        <button onClick={savePay} disabled={savingPay} data-testid="save-settings-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {savingPay ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Enregistrer</>}
        </button>
      </Card>

      <Card title="Frais de livraison" subtitle="Tarifs par zone + numéro WhatsApp pour les zones hors liste.">
        <div className="space-y-2">
          {deliv.zones.map((z, i) => (
            <div key={i} className="flex items-center gap-3">
              <input className={inputCls} value={z.label} onChange={setZone(i, "label")} data-testid={`zone-label-${i}`} />
              <div className="relative w-40 shrink-0">
                <input type="number" className={inputCls + " pr-12"} value={z.fee} onChange={setZone(i, "fee")} data-testid={`zone-fee-${i}`} />
                <span className="absolute right-3 top-2.5 text-xs text-brand-muted">FCFA</span>
              </div>
            </div>
          ))}
        </div>
        <div>
          <label className="text-sm font-medium text-brand-sub">Numéro WhatsApp (zones hors liste)</label>
          <input className={inputCls + " mt-1"} value={deliv.whatsapp_number} onChange={setD("whatsapp_number")} placeholder="+228 ..." data-testid="delivery-whatsapp" />
        </div>
        <div>
          <label className="text-sm font-medium text-brand-sub">Message « autre zone »</label>
          <textarea rows={2} className={inputCls + " mt-1"} value={deliv.other_zone_note} onChange={setD("other_zone_note")} />
        </div>
        <div>
          <label className="text-sm font-medium text-brand-sub">Livraison offerte à partir de (FCFA)</label>
          <div className="relative w-52 mt-1">
            <input type="number" className={inputCls + " pr-12"} value={deliv.free_delivery_threshold} onChange={setD("free_delivery_threshold")} data-testid="free-delivery-threshold" placeholder="Ex: 25000" />
            <span className="absolute right-3 top-2.5 text-xs text-brand-muted">FCFA</span>
          </div>
          <p className="text-xs text-brand-muted mt-1">Au-dessus de ce montant, la livraison passe à 0 FCFA. Mettez 0 pour désactiver.</p>
        </div>
        <button onClick={saveDeliv} disabled={savingDeliv} data-testid="save-delivery-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {savingDeliv ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Enregistrer la livraison</>}
        </button>
      </Card>

      <Card title="Commission vendeur" subtitle="Pourcentage versé au vendeur sur chaque vente qu'il enregistre (calculé sur le montant des produits, une fois la commande payée).">
        <div>
          <label className="text-sm font-medium text-brand-sub">Taux de commission (%)</label>
          <div className="relative w-52 mt-1">
            <input type="number" step="0.5" className={inputCls + " pr-10"} value={comm.vendeur_rate} onChange={(e) => setComm({ vendeur_rate: e.target.value })} data-testid="commission-rate-input" placeholder="5" />
            <span className="absolute right-3 top-2.5 text-xs text-brand-muted">%</span>
          </div>
          <p className="text-xs text-brand-muted mt-1">Ex : 5 % → sur une vente de 10 000 FCFA de produits, le vendeur gagne 500 FCFA. Mettez 0 pour désactiver.</p>
        </div>
        <button onClick={saveComm} disabled={savingComm} data-testid="save-commission-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {savingComm ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Enregistrer la commission</>}
        </button>
      </Card>

      <Card title="Alerte caisse vendeur" subtitle="Vous serez alerté (en rouge sur le tableau de bord) quand le solde à remettre d'un vendeur dépasse ce montant.">
        <div>
          <label className="text-sm font-medium text-brand-sub">Seuil d'alerte (FCFA)</label>
          <div className="relative w-60 mt-1">
            <input type="number" className={inputCls + " pr-12"} value={caisse.alert_threshold} onChange={(e) => setCaisse({ alert_threshold: e.target.value })} data-testid="caisse-threshold-input" placeholder="50000" />
            <span className="absolute right-3 top-2.5 text-xs text-brand-muted">FCFA</span>
          </div>
          <p className="text-xs text-brand-muted mt-1">Ex : 50 000 → si un vendeur a plus de 50 000 FCFA d'argent liquide non versé, il apparaît en alerte.</p>
        </div>
        <button onClick={saveCaisse} disabled={savingCaisse} data-testid="save-caisse-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {savingCaisse ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Enregistrer le seuil</>}
        </button>
      </Card>

      <Card title="Lieu de pointage (vendeur)" subtitle="Le vendeur ne peut pointer (arrivée, pause, départ) que s'il se trouve sur place. Choisissez une des 3 méthodes ci-dessous.">
        {/* Méthode 1 : recherche par adresse */}
        <div>
          <label className="text-sm font-medium text-brand-sub">1. Rechercher par adresse du bureau</label>
          <div className="flex flex-wrap gap-2 mt-1">
            <input className={inputCls + " flex-1 min-w-[220px]"} value={addrQuery} onChange={(e) => setAddrQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchAddress()} placeholder="Ex: Boulevard du 13 Janvier, Lomé, Togo" data-testid="attendance-address-input" />
            <button type="button" onClick={searchAddress} disabled={searchingAddr} data-testid="search-address-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-5 py-2.5 font-semibold flex items-center gap-2 disabled:opacity-60">
              {searchingAddr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Rechercher
            </button>
          </div>
          {addrResults && addrResults.length > 0 && (
            <div className="mt-2 space-y-1.5" data-testid="attendance-address-results">
              {addrResults.map((r, i) => (
                <button key={i} type="button" onClick={() => pickAddress(r)} data-testid={`address-result-${i}`} className="w-full text-left text-sm border border-brand-line hover:border-brand hover:bg-brand-cream rounded-xl px-3 py-2 transition-colors">
                  <MapPin className="w-3.5 h-3.5 text-brand inline mr-1" /> {r.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Méthode 2 : position actuelle */}
        <div>
          <label className="text-sm font-medium text-brand-sub">2. Utiliser ma position actuelle <span className="text-brand-muted font-normal">(uniquement si vous êtes sur place)</span></label>
          <div className="mt-1">
            <button type="button" onClick={captureLocation} disabled={locating} data-testid="capture-location-button" className="bg-white border border-brand-line text-brand-ink hover:border-brand rounded-full px-5 py-2.5 font-semibold flex items-center gap-2 disabled:opacity-60">
              {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4 text-brand" />} Utiliser ma position actuelle
            </button>
          </div>
        </div>

        {/* Méthode 3 : coordonnées manuelles */}
        <div>
          <label className="text-sm font-medium text-brand-sub">3. Saisir les coordonnées GPS <span className="text-brand-muted font-normal">(copiées depuis Google Maps)</span></label>
          <div className="grid grid-cols-2 gap-3 mt-1 max-w-md">
            <input type="number" step="any" className={inputCls} value={att.lat ?? ""} onChange={(e) => setAtt({ ...att, lat: e.target.value === "" ? null : Number(e.target.value) })} placeholder="Latitude (ex: 6.13190)" data-testid="attendance-lat-input" />
            <input type="number" step="any" className={inputCls} value={att.lng ?? ""} onChange={(e) => setAtt({ ...att, lng: e.target.value === "" ? null : Number(e.target.value) })} placeholder="Longitude (ex: 1.22280)" data-testid="attendance-lng-input" />
          </div>
          <p className="text-xs text-brand-muted mt-1">Sur Google Maps : clic droit sur le bureau → cliquez sur les chiffres (ex. « 6.13190, 1.22280 ») pour les copier, puis collez ici.</p>
        </div>

        {att.lat != null && att.lng != null && (
          <span className="inline-flex items-center gap-1.5 text-sm text-brand-sub bg-brand-cream rounded-full px-3 py-1.5" data-testid="attendance-coords">
            <MapPin className="w-4 h-4 text-brand" /> Position choisie : {Number(att.lat).toFixed(5)}, {Number(att.lng).toFixed(5)}
          </span>
        )}

        <div>
          <label className="text-sm font-medium text-brand-sub">Rayon autorisé (mètres)</label>
          <div className="relative w-52 mt-1">
            <input type="number" className={inputCls + " pr-10"} value={att.radius_m} onChange={(e) => setAtt({ ...att, radius_m: e.target.value })} data-testid="attendance-radius" placeholder="200" />
            <span className="absolute right-3 top-2.5 text-xs text-brand-muted">m</span>
          </div>
          <p className="text-xs text-brand-muted mt-1">Le vendeur doit être à moins de ce rayon pour pointer. Recommandé : 150–300 m.</p>
        </div>
        <button onClick={saveAtt} disabled={savingAtt} data-testid="save-attendance-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {savingAtt ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Enregistrer le lieu de pointage</>}
        </button>
      </Card>

      <Card title="Codes promo" subtitle="Réductions à saisir par le client au moment de payer.">
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Code (ex: NOUMABOU10)" value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" />
          <select className={inputCls} value={newPromo.type} onChange={(e) => setNewPromo({ ...newPromo, type: e.target.value })} data-testid="promo-type-select">
            <option value="percent">Pourcentage (%)</option>
            <option value="fixed">Montant fixe (FCFA)</option>
          </select>
          <input type="number" className={inputCls} placeholder={newPromo.type === "percent" ? "Valeur (ex: 10 pour 10%)" : "Valeur (ex: 1000 FCFA)"} value={newPromo.value} onChange={(e) => setNewPromo({ ...newPromo, value: e.target.value })} data-testid="promo-value-input" />
          <input type="number" className={inputCls} placeholder="Commande min. (FCFA, optionnel)" value={newPromo.min_order} onChange={(e) => setNewPromo({ ...newPromo, min_order: e.target.value })} />
          <div className="sm:col-span-2">
            <label className="text-xs text-brand-muted">Expiration (optionnel)</label>
            <input type="date" className={inputCls + " mt-1"} value={newPromo.expires_at ? newPromo.expires_at.slice(0, 10) : ""} onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="promo-expires-input" />
          </div>
        </div>
        <button onClick={addPromo} data-testid="add-promo-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-2.5 font-semibold flex items-center gap-2">
          <Save className="w-4 h-4" /> Créer le code
        </button>
        <div className="space-y-2 pt-2">
          {promos.length === 0 ? <p className="text-sm text-brand-muted">Aucun code promo.</p> : promos.map((p) => (
            <div key={p.id} data-testid={`promo-row-${p.id}`} className="flex items-center justify-between border border-brand-line rounded-xl px-4 py-2.5">
              <div className="text-sm">
                <span className="font-bold text-brand-ink">{p.code}</span>
                <span className="text-brand-sub ml-2">{p.type === "percent" ? `-${p.value}%` : `-${p.value} FCFA`}</span>
                {p.min_order > 0 && <span className="text-xs text-brand-muted ml-2">dès {p.min_order} FCFA</span>}
                {p.expires_at && <span className="text-xs text-brand-muted ml-2">exp. {p.expires_at.slice(0, 10)}</span>}
              </div>
              <button onClick={() => deletePromo(p.id)} data-testid={`delete-promo-${p.id}`} className="text-brand-secondary hover:bg-red-50 rounded-lg p-1.5 text-sm">Supprimer</button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Ministère — Nourrir l'âme" subtitle="Bio, livres, réseaux et vidéos.">
        <div>
          <label className="text-sm font-medium text-brand-sub">Nom de l'auteur</label>
          <input data-testid="settings-author-name" className={inputCls + " mt-1"} value={site.author_name} onChange={setS("author_name")} />
        </div>
        <div>
          <label className="text-sm font-medium text-brand-sub">Bio</label>
          <textarea rows={4} data-testid="settings-author-bio" className={inputCls + " mt-1"} value={site.author_bio} onChange={setS("author_bio")} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium text-brand-sub">TikTok Ministère (lien)</label><input className={inputCls + " mt-1"} value={site.tiktok_ministry_url} onChange={setS("tiktok_ministry_url")} placeholder="https://tiktok.com/@..." /></div>
          <div><label className="text-sm font-medium text-brand-sub">TikTok Boutique (lien)</label><input className={inputCls + " mt-1"} value={site.tiktok_boutique_url} onChange={setS("tiktok_boutique_url")} placeholder="https://tiktok.com/@..." /></div>
        </div>
        <div><label className="text-sm font-medium text-brand-sub">Chaîne YouTube (lien)</label><input className={inputCls + " mt-1"} value={site.youtube_channel_url} onChange={setS("youtube_channel_url")} placeholder="https://youtube.com/@..." /></div>
        <div><label className="text-sm font-medium text-brand-sub">Vidéos YouTube (une URL par ligne)</label><textarea rows={3} className={inputCls + " mt-1"} value={site.youtube_videos} onChange={setS("youtube_videos")} placeholder="https://youtu.be/xxxxxxxxxxx" /></div>
        <div><label className="text-sm font-medium text-brand-sub">Note application Bible</label><textarea rows={2} className={inputCls + " mt-1"} value={site.bible_app_note} onChange={setS("bible_app_note")} /></div>

        <div className="space-y-3">
          <p className="text-sm font-semibold text-brand-ink flex items-center gap-2"><BookOpen className="w-4 h-4" /> Livres</p>
          {site.books.map((b, i) => (
            <div key={i} className="border border-brand-line rounded-xl p-4 grid sm:grid-cols-[80px,1fr] gap-4">
              <div>
                <div className="w-20 h-24 rounded-lg bg-brand-cream overflow-hidden flex items-center justify-center">
                  {b.cover_path ? <img src={FILE_BASE + b.cover_path} alt="" className="w-full h-full object-cover" /> : <BookOpen className="w-6 h-6 text-brand-muted" />}
                </div>
                <label className="mt-1 flex items-center gap-1 text-xs text-brand cursor-pointer"><Upload className="w-3 h-3" /> Couverture<input type="file" accept="image/*" className="hidden" onChange={uploadCover(i)} /></label>
              </div>
              <div className="space-y-2">
                <input className={inputCls} placeholder="Titre du livre" value={b.title} onChange={setBook(i, "title")} data-testid={`book-title-${i}`} />
                <input className={inputCls} placeholder="Sous-titre / description courte" value={b.subtitle} onChange={setBook(i, "subtitle")} />
                <input className={inputCls} placeholder="Lien Amazon" value={b.amazon_url} onChange={setBook(i, "amazon_url")} data-testid={`book-amazon-${i}`} />
              </div>
            </div>
          ))}
        </div>

        <button onClick={saveSite} disabled={savingSite} data-testid="save-site-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
          {savingSite ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5" /> Enregistrer le ministère</>}
        </button>
      </Card>

      <div className="flex items-start gap-2 text-xs text-brand-muted bg-brand-cream rounded-lg p-3">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        Ces informations s'affichent sur la page publique « Ministère » et à la boutique. Modifiez-les quand vous voulez.
      </div>
    </div>
  );
}
